import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Search, Filter } from 'lucide-react';
import AddExperienceModal from '../Experience/AddExperienceModal';
import { useUserContext } from '../../contexts/UserContext';
import { getAllActivityData } from '../../utils/APIs';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { formatDate } from '../../utils/converter';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import { Card, Button, Input, SegmentedControl, AgTableCard } from '../UI';

const ManageOffers = () => {
    const { enums, content } = useContentfulContext();

    const [rowData, setRowData] = useState([]);
    const [showAddExperiencesModal, setShowAddExperiencesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('json'); // 'json' | 'all'

    const { userData } = useUserContext();
    const navigator = useNavigate();

    useEffect(() => {
        if (userData.defaultAdobeIO) {
            getActivityData();
        }
    }, [userData.defaultAdobeIO]);

    const getActivityData = async () => {
        try {
            setLoading(true);
            const result = await getAllActivityData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                userData.defaultAdobeIO?.tenant_name,
                content.tasks.offers.id
            );
            setError("");
            setRowData(result.data.offers || []);
        } catch (err) {
            setError(err?.message || content?.COMMON_MESSAGE?.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const onGridReady = (params) => {
        params.api.setFilterModel({
            type: { type: 'contains', filter: 'json' }
        });
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-offers-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'modifiedAt', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "name",
                    minWidth: 260,
                },
                {
                    colId: "type",
                    minWidth: 160,
                },
                {
                    colId: "workspace",
                    minWidth: 160,
                },
                {
                    colId: "associated_experiences",
                    minWidth: 200,
                },
                {
                    colId: "modifiedAt",
                    minWidth: 160,
                },
                {
                    colId: "actions",
                    minWidth: 60,
                }
            ],
        };
    }, []);

    const handleCloseModal = () => {
        setShowAddExperiencesModal(false);
    };

    const onFilterTextBoxChanged = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (gridApi) {
            gridApi.setFilterModel({
                ...gridApi.getFilterModel(),
                name: value ? { type: 'contains', filter: value.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            const currentFilter = gridApi.getFilterModel();
            delete currentFilter.name;
            gridApi.setFilterModel(currentFilter);
        }
    };

    const handleTypeFilterChange = (filterType) => {
        setTypeFilter(filterType);
        if (gridApi) {
            const currentFilter = gridApi.getFilterModel();
            if (filterType === 'all') {
                delete currentFilter.type;
            } else {
                currentFilter.type = { type: 'contains', filter: 'json' };
            }
            gridApi.setFilterModel(currentFilter);
        }
    };

    const colDefs = [
        {
            headerName: content.LIST_OFFERS_CONTENT.offers_table_headers.offer_name,
            field: "name",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:text-primary-hover hover:underline text-left"
                    onClick={() => navigator(`/experience/edit-offer/${data.type}-${data.id}`)}
                >
                    {data.name}
                </button>
            ),
            filter: true,
            suppressMovable: true
        },
        {
            headerName: content.LIST_OFFERS_CONTENT.offers_table_headers.offer_type,
            field: "type",
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ value }) => (
                <span className="stride-badge-pill bg-slate-100 text-slate-700 uppercase font-mono text-[11px]">
                    {value || 'N/A'}
                </span>
            )
        },
        {
            headerName: "Workspace",
            field: "workspace",
            sortable: true,
            filter: true,
            resizable: true,
            cellRenderer: ({ value }) => userData?.workspacesMap?.[value] || value || "NA",
            filterValueGetter: (params) => {
                const val = userData?.workspacesMap?.[params.data.workspace] || params.data.workspace || "NA";
                return val ? val.toString().toLowerCase() : '';
            },
        },
        {
            headerName: "Experiences",
            field: "associated_experiences",
            sortable: true,
            filter: true,
            resizable: true,
            cellRenderer: ({ value }) => {
                if (!value || value.length === 0) return <span className="text-stride-muted">NA</span>;
                return (
                    <span className="truncate" title={value.map(exp => exp.experience_name).join(", ")}>
                        {value.map(exp => exp.experience_name).join(", ")}
                    </span>
                );
            },
            filterValueGetter: (params) => {
                const exps = params.data?.associated_experiences;
                if (!exps || exps.length === 0) return '';
                return exps.map(e => e.experience_name.toLowerCase()).join(" ");
            },
        },
        {
            headerName: content.LIST_OFFERS_CONTENT.offers_table_headers.modified_at,
            field: "modifiedAt",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.modifiedAt)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ({ data }) => {
                return (
                    <div className="stride-table-actions">
                        <button
                            type="button"
                            title="Edit Offer"
                            className="stride-table-btn stride-table-btn-edit"
                            onClick={() => navigator(`/experience/edit-offer/${data.type}-${data.id}`)}
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            },
            width: 70,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true
        },
    ];

    return (
        <>
            {error && <ErrorAlert timeout={content.LIST_OFFERS_CONTENT.timeout}>{error}</ErrorAlert>}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Experiences / Manage</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            {content.LIST_OFFERS_CONTENT.title}
                        </h1>
                        <p className="text-sm text-stride-muted mt-1">
                            {rowData?.length || 0} total offers synced with Adobe Target
                        </p>
                    </div>
                </div>

                {/* Empty State: No Adobe IO */}
                {userData?.adobeIoIntegrations?.length === 0 && (
                    <Card className="text-center p-12 max-w-lg mx-auto">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            {content.LIST_OFFERS_CONTENT.no_adobe_io}
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigator('/setup/new-integration')}
                        >
                            {content.LIST_OFFERS_CONTENT.create_new_adobe_io}
                        </Button>
                    </Card>
                )}

                <AddExperienceModal
                    showAddExperiencesModal={showAddExperiencesModal}
                    handleCloseModal={handleCloseModal}
                />

                {/* Quartz AG-Grid Table inside Card with Search & Filter Header */}
                {userData.defaultAdobeIO && (
                    <AgTableCard
                        header={
                            <>
                                <div className="w-full md:w-80">
                                    <Input
                                        placeholder="Search by name..."
                                        value={searchTerm}
                                        onChange={onFilterTextBoxChanged}
                                        prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                        clearable
                                        onClear={handleClearSearch}
                                    />
                                </div>

                                {/* Filter Pill Controls */}
                                <SegmentedControl
                                    value={typeFilter}
                                    onChange={handleTypeFilterChange}
                                    variant="primary"
                                    size="md"
                                    options={[
                                        { value: 'json', label: 'JSON Only', icon: <Filter className="w-3.5 h-3.5" /> },
                                        { value: 'all', label: 'All Offers' },
                                    ]}
                                />
                            </>
                        }
                        tableId="manage-offers-table"
                        loading={loading}
                        rowData={rowData}
                        columnDefs={colDefs}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        pagination={true}
                        paginationPageSize={content.LIST_OFFERS_CONTENT.default_page_size}
                        paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
                    />
                )}
            </main>
        </>
    );
};

export default ManageOffers;
